import { useState, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug],
  );

  if (!slug || !categories.find((c) => c.slug === slug)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {currentCategory?.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <article
                    key={article.slug}
                    className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer border border-background-200/70 hover:border-background-300/70 transition-all duration-200"
                  >
                    <Link to={`/article/${article.slug}`} className="block">
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
                        </div>
                        <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
                          {article.excerpt}
                        </p>
                        <div className="mt-4 flex items-center gap-1 text-sm text-primary-500 font-medium">
                          阅读全文
                          <i className="ri-arrow-right-line text-xs"></i>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}